import { Button, Spin, Typography } from "antd"
import { AnimatePresence } from "motion/react"
import { useCallback, useMemo } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import { EVALUATION_STATUS } from "#customTypes/composedTypes/evaluationTypes"

import EvaluationWorkloadProgressRow, {
  STATUS_SEGMENTS,
} from "./EvaluationWorkloadProgressRow"
import useEvaluationViewContext from "./useEvaluationViewContext"
import useEvaluationWorkloadDashboard from "./useEvaluationWorkloadDashboard"

const { Text } = Typography

const EvaluationWorkloadDashboard = () => {
  const {
    classesById,
    classSessionsById,
    evaluationsById,
    usersById,
    statusFilters,
    teacherFilters,
    approverFilters,
    setStatusFilters,
    setTeacherFilters,
    setApproverFilters,
    problemFlagsByEvaluationId,
    isDashboardReady,
  } = useEvaluationViewContext((state) => ({
    classesById: state.classesById,
    classSessionsById: state.classSessionsById,
    evaluationsById: state.evaluationsById,
    usersById: state.usersById,
    statusFilters: state.statusFilters,
    teacherFilters: state.teacherFilters,
    approverFilters: state.approverFilters,
    setStatusFilters: state.setStatusFilters,
    setTeacherFilters: state.setTeacherFilters,
    setApproverFilters: state.setApproverFilters,
    problemFlagsByEvaluationId: state.problemFlagsByEvaluationId,
    isDashboardReady: state.isDashboardReady,
  }))

  const dashboardEvaluationsById = useMemo(() => {
    return Object.fromEntries(
      Object.entries(evaluationsById).filter(([, evaluation]) => {
        const classObj = classesById[evaluation.classId]
        if (!classObj || classObj.isSummerCamp) return false

        const isStudentInAnySession = (classObj.sessionIds || []).some(
          (sessionId) =>
            classSessionsById[sessionId]?.studentIds?.includes(
              evaluation.studentId
            )
        )

        return isStudentInAnySession
      })
    )
  }, [classesById, classSessionsById, evaluationsById])

  const {
    teacherRows,
    approverRows,
    scopedEvalCount,
    toggleTeacherFilter,
    toggleApproverFilter,
  } = useEvaluationWorkloadDashboard({
    evaluationsById: dashboardEvaluationsById,
    usersById,
    statusFilters,
    teacherFilters,
    approverFilters,
    setTeacherFilters,
    setApproverFilters,
    problemFlagsByEvaluationId,
  })

  const maxTotal = useMemo(
    () => Math.max(...teacherRows.map((row) => row.total), 1),
    [teacherRows]
  )
  const maxApproverTotal = useMemo(
    () => Math.max(...approverRows.map((row) => row.total), 1),
    [approverRows]
  )
  const hasActiveFilters =
    teacherFilters.length > 0 ||
    statusFilters.length > 0 ||
    approverFilters.length > 0

  const onStatusSegmentClick = useCallback(
    (status: EVALUATION_STATUS) => {
      const isSameStatus =
        statusFilters.length === 1 && statusFilters[0] === status

      if (isSameStatus) {
        setStatusFilters([])
        return
      }

      setStatusFilters([status])
    },
    [setStatusFilters, statusFilters]
  )

  if (!isDashboardReady) {
    return (
      <Flex
        col
        gap={12}
        style={{
          width: 420,
          padding: 16,
          borderRadius: 10,
          background: "#ffffff",
          alignItems: "center",
        }}
      >
        <Spin size="small" />
        <Text type="secondary">Loading class and session data...</Text>
      </Flex>
    )
  }

  return (
    <Flex
      col
      gap={14}
      style={{
        width: 980,
        maxHeight: 620,
        overflow: "hidden",
        padding: 8,
        borderRadius: 10,
        background: "#ffffff",
      }}
    >
      <Flex
        gap={8}
        style={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Text strong>Evaluation Workload Snapshot</Text>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Text type="secondary">Scoped evals: {scopedEvalCount}</Text>
          <Button
            size="small"
            type={hasActiveFilters ? "primary" : "default"}
            danger={hasActiveFilters}
            onClick={() => {
              setTeacherFilters([])
              setStatusFilters([])
              setApproverFilters([])
            }}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </Button>
        </Flex>
      </Flex>
      <AnimatePresence mode="popLayout">
        {hasActiveFilters ? (
          <AnimateInFlex
            key="active-filters-hint"
            animation={ANIMATION_BY_KEY.leftSm}
          >
            <Text style={{ color: "#d46b08" }}>
              Filters are active. Click again to toggle.
            </Text>
          </AnimateInFlex>
        ) : null}
      </AnimatePresence>

      <Flex
        col
        gap={24}
        style={{ overflow: "auto", maxHeight: 560, paddingRight: 20 }}
      >
        <Flex col gap={8}>
          <Flex
            gap={8}
            style={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text strong style={{ fontSize: 24 }}>
              Approver Progress
            </Text>
          </Flex>
          <Text type="secondary">
            Click an approver name or status segment to filter results
          </Text>

          <Flex col gap={6}>
            <AnimatePresence mode="sync">
              {approverRows.map((row) => {
                const isActive =
                  approverFilters.length === 1 &&
                  approverFilters[0] === row.approverId
                const barTooltipTitle = `Complete ${row.complete}/${row.total} | Ongoing ${row.ongoing} | Problem ${row.problem}${row.problematicCount > 0 ? ` | Flagged ${row.problematicCount}` : ""}`

                return (
                  <AnimateInFlex
                    key={row.approverId}
                    animation={ANIMATION_BY_KEY.leftSm}
                  >
                    <EvaluationWorkloadProgressRow
                      personId={row.approverId}
                      personName={row.approverName}
                      total={row.total}
                      completeCount={row.complete}
                      statusCounts={row.statusCounts}
                      maxTotal={maxApproverTotal}
                      isActive={isActive}
                      isFullyComplete={
                        row.baselineTotal > 0 &&
                        row.baselineComplete === row.baselineTotal
                      }
                      statusFilters={statusFilters}
                      onToggleFilter={toggleApproverFilter}
                      onStatusSegmentClick={onStatusSegmentClick}
                      badgeOverflowCount={9999}
                      segmentKeyPrefix="approver-segment"
                      barTooltipTitle={barTooltipTitle}
                    />
                  </AnimateInFlex>
                )
              })}
            </AnimatePresence>
          </Flex>
        </Flex>

        <Flex col gap={8}>
          <Text strong style={{ fontSize: 24 }}>
            Teacher Progress
          </Text>
          <Text type="secondary">
            Click a teacher name or status segment to filter results
          </Text>
          <Flex gap={12} style={{ flexWrap: "wrap", marginBottom: 2 }}>
            {STATUS_SEGMENTS.map((segment) => (
              <Flex
                key={`status-legend-${segment.status}`}
                gap={6}
                style={{ alignItems: "center" }}
              >
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    backgroundColor: segment.color,
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
                  }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {segment.label}
                </Text>
              </Flex>
            ))}
          </Flex>
          <Flex col gap={6}>
            <AnimatePresence mode="sync">
              {teacherRows.map((row) => {
                const isActive =
                  teacherFilters.length === 1 &&
                  teacherFilters[0] === row.teacherId
                return (
                  <AnimateInFlex
                    key={row.teacherId}
                    animation={ANIMATION_BY_KEY.leftSm}
                  >
                    <EvaluationWorkloadProgressRow
                      personId={row.teacherId}
                      personName={row.teacherName}
                      total={row.total}
                      completeCount={row.completeCount}
                      statusCounts={row.statusCounts}
                      maxTotal={maxTotal}
                      isActive={isActive}
                      isFullyComplete={
                        row.baselineTotal > 0 &&
                        row.baselineCompleteCount === row.baselineTotal
                      }
                      statusFilters={statusFilters}
                      onToggleFilter={toggleTeacherFilter}
                      onStatusSegmentClick={onStatusSegmentClick}
                      badgeOverflowCount={999}
                      segmentKeyPrefix="teacher-segment"
                    />
                  </AnimateInFlex>
                )
              })}
            </AnimatePresence>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default EvaluationWorkloadDashboard
